import { useEffect, useRef, useState } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { Box } from "@mui/material";

import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

import { addOrUpdateEvents } from "./layers/symbolLayer";
import { addOrUpdateRoutes } from "./layers/routesLayer";
import { addOrUpdateAreas } from "./layers/areasLayer";

import mapConfig, { MAPBOX_ACCESS_TOKEN } from "./mapConfig";

import {
  addOrUpdateNetwork,
  collectPathIdsFromGeometry,
  setNetworkVisibility,
} from "./helperFunctions";

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

const scenarioMapConfig = mapConfig;
const networkLayerConfig = scenarioMapConfig.layers.network;
const NETWORK_LAYER_ID = networkLayerConfig.layerId;
const EVENTS_SOURCE_ID = "scenario-events-source";
const EVENTS_LAYER_ID = "scenario-events-layer";
const ROUTES_SOURCE_ID = "scenario-routes-source";
const ROUTES_LAYER_ID = "scenario-routes-layer";
const AREAS_SOURCE_ID = "scenario-areas-source";
const AREAS_FILL_LAYER_ID = "scenario-areas-fill-layer";
const AREAS_OUTLINE_LAYER_ID = "scenario-areas-outline-layer";

function ScenarioMap({
  value,
  onChange,
  network,
  monitoringItems,
  routesData = [],
  areasData = [],
}) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const drawRef = useRef(null);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [styleRevision, setStyleRevision] = useState(0);

  const selectedIds = value.kpi_monitoring_ids || [];
  const geometryRef = useRef(value.location || null);
  const activeModeRef = useRef(scenarioMapConfig.draw.defaultMode);

  const hoverPopupRef = useRef(
    new mapboxgl.Popup({ closeButton: false, closeOnClick: false })
  );
  const eventHoverPopupRef = useRef(
    new mapboxgl.Popup({ closeButton: false, closeOnClick: false })
  );

  const networkHoverMoveHandlerRef = useRef(null);
  const networkHoverLeaveHandlerRef = useRef(null);

  const isStyleReady = (map) => !!map && map.isStyleLoaded();

  const moveLayerToTop = (map, layerId) => {
    if (!map?.getLayer(layerId)) return;
    map.moveLayer(layerId);
  };

  const ensureScenarioSourcesAndLayers = (map) => {
    if (!isStyleReady(map)) return false;

    if (!map.getSource(EVENTS_SOURCE_ID)) {
      map.addSource(EVENTS_SOURCE_ID, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
    }

    if (!map.getLayer(EVENTS_LAYER_ID)) {
      map.addLayer({
        id: EVENTS_LAYER_ID,
        type: "symbol",
        source: EVENTS_SOURCE_ID,
        layout: {
          "icon-image": ["concat", "event-icon-", ["get", "id"]],
          "icon-size": [
            "case",
            ["boolean", ["get", "selected"], false],
            1.3,
            0.9,
          ],
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          visibility: "none",
        },
      });
    }

    if (!map.getSource(ROUTES_SOURCE_ID)) {
      map.addSource(ROUTES_SOURCE_ID, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
    }

    if (!map.getLayer(ROUTES_LAYER_ID)) {
      map.addLayer({
        id: ROUTES_LAYER_ID,
        type: "line",
        source: ROUTES_SOURCE_ID,
        layout: {
          "line-join": "round",
          "line-cap": "round",
          visibility: "none",
        },
        paint: {
          "line-color": ["coalesce", ["get", "color"], "#3388ff"],
          "line-width": [
            "case",
            ["boolean", ["get", "selected"], false],
            5,
            3,
          ],
          "line-opacity": [
            "case",
            ["boolean", ["get", "selected"], false],
            1.0,
            0.4,
          ],
        },
      });
    }

    if (!map.getSource(AREAS_SOURCE_ID)) {
      map.addSource(AREAS_SOURCE_ID, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
    }

    if (!map.getLayer(AREAS_FILL_LAYER_ID)) {
      map.addLayer({
        id: AREAS_FILL_LAYER_ID,
        type: "fill",
        source: AREAS_SOURCE_ID,
        layout: {
          visibility: "none",
        },
        paint: {
          "fill-color": [
            "case",
            ["boolean", ["get", "selected"], false],
            "#202940",
            "#494F59",
          ],
          "fill-opacity": [
            "case",
            ["boolean", ["get", "selected"], false],
            0.35,
            0.15,
          ],
        },
      });
    }

    if (!map.getLayer(AREAS_OUTLINE_LAYER_ID)) {
      map.addLayer({
        id: AREAS_OUTLINE_LAYER_ID,
        type: "line",
        source: AREAS_SOURCE_ID,
        layout: {
          "line-join": "round",
          "line-cap": "round",
          visibility: "none",
        },
        paint: {
          "line-color": "#ffffff",
          "line-width": [
            "case",
            ["boolean", ["get", "selected"], false],
            3,
            1.3,
          ],
          "line-opacity": 0.9,
        },
      });
    }

    moveLayerToTop(map, AREAS_FILL_LAYER_ID);
    moveLayerToTop(map, AREAS_OUTLINE_LAYER_ID);
    moveLayerToTop(map, ROUTES_LAYER_ID);
    moveLayerToTop(map, EVENTS_LAYER_ID);
    moveLayerToTop(map, AREAS_FILL_LAYER_ID);
    moveLayerToTop(map, AREAS_OUTLINE_LAYER_ID);

    return true;
  };

  const attachHoverHandlers = (map) => {
    if (!map || !map.getLayer(NETWORK_LAYER_ID)) return;

    if (networkHoverMoveHandlerRef.current) {
      map.off("mousemove", NETWORK_LAYER_ID, networkHoverMoveHandlerRef.current);
      networkHoverMoveHandlerRef.current = null;
    }
    if (networkHoverLeaveHandlerRef.current) {
      map.off("mouseleave", NETWORK_LAYER_ID, networkHoverLeaveHandlerRef.current);
      networkHoverLeaveHandlerRef.current = null;
    }

    const onMove = (e) => {
      if (!e.features || !e.features.length) return;
      const feature = e.features[0];
      const { name } = feature.properties || {};
      hoverPopupRef.current
        .setLngLat(e.lngLat)
        .setHTML(`<strong>${name || "Segment"}</strong>`)
        .addTo(map);
    };

    const onLeave = () => {
      hoverPopupRef.current.remove();
    };

    networkHoverMoveHandlerRef.current = onMove;
    networkHoverLeaveHandlerRef.current = onLeave;

    map.on("mousemove", NETWORK_LAYER_ID, onMove);
    map.on("mouseleave", NETWORK_LAYER_ID, onLeave);
  };

  useEffect(() => {
    if (!mapContainerRef.current) return;
    if (mapRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: scenarioMapConfig.map.style,
      center: scenarioMapConfig.map.center,
      zoom: scenarioMapConfig.map.zoom,
      minZoom: scenarioMapConfig.map.minZoom,
      maxZoom: scenarioMapConfig.map.maxZoom,
      pitch: scenarioMapConfig.map.pitch,
      bearing: scenarioMapConfig.map.bearing,
    });

    mapRef.current = map;

    map.on("load", () => {
      setMapLoaded(true);
      ensureScenarioSourcesAndLayers(map);
      setStyleRevision((prev) => prev + 1);

      const draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: scenarioMapConfig.draw.controls,
        defaultMode: scenarioMapConfig.draw.defaultMode,
      });

      map.addControl(draw, "top-right");
      drawRef.current = draw;

      if (network) {
        addOrUpdateNetwork(map, network);
        setNetworkVisibility(map, false);
        attachHoverHandlers(map);
      }

      if (geometryRef.current) {
        draw.add({
          type: "Feature",
          properties: {},
          geometry: geometryRef.current,
        });
      }

      const blockIfDrawing = (e) => {
        const mode = activeModeRef.current;
        const isDrawing = mode === "draw_line_string" || mode === "draw_point";

        if (!isDrawing) return;
        if (!map.getLayer(NETWORK_LAYER_ID)) return;

        const hits = map.queryRenderedFeatures(e.point, {
          layers: [NETWORK_LAYER_ID],
        });

        if (!hits.length) {
          e.preventDefault();

          if (e.originalEvent.stopPropagation) {
            e.originalEvent.stopPropagation();
          }
          if (e.originalEvent.stopImmediatePropagation) {
            e.originalEvent.stopImmediatePropagation();
          }
        }
      };

      map.on("mousedown", blockIfDrawing);
      map.on("click", blockIfDrawing);

      map.on("draw.modechange", (e) => {
        const mode = e.mode;
        activeModeRef.current = mode;

        const isDrawing = mode === "draw_line_string" || mode === "draw_point";

        if (isDrawing && network) {
          addOrUpdateNetwork(map, network);
          setNetworkVisibility(map, true);
        } else {
          setNetworkVisibility(map, false);
          map.getCanvas().style.cursor = "";
        }
      });

      map.on("mousemove", (e) => {
        const mode = activeModeRef.current;
        const isDrawing = mode === "draw_line_string" || mode === "draw_point";

        if (!isDrawing) {
          map.getCanvas().style.cursor = "";
          return;
        }

        if (!map.getLayer(NETWORK_LAYER_ID)) {
          map.getCanvas().style.cursor = "not-allowed";
          return;
        }

        const hits = map.queryRenderedFeatures(e.point, {
          layers: [NETWORK_LAYER_ID],
        });

        map.getCanvas().style.cursor = hits.length > 0 ? "crosshair" : "not-allowed";
      });

      const syncFromDraw = (e) => {
        if (!drawRef.current) return;

        const currentMap = mapRef.current;
        const fc = drawRef.current.getAll();

        if (!fc.features.length) {
          geometryRef.current = null;
          onChange({ location: null, location_path_ids: [] });
          setNetworkVisibility(currentMap, false);
          return;
        }

        const lastFeature =
          (e?.features && e.features[e.features.length - 1]) ||
          fc.features[fc.features.length - 1];

        const geometry = lastFeature.geometry;
        const pathIds = collectPathIdsFromGeometry(currentMap, geometry);

        if (!pathIds.length) {
          drawRef.current.deleteAll();
          geometryRef.current = null;
          onChange({ location: null, location_path_ids: [] });
          setNetworkVisibility(currentMap, true);
          return;
        }

        geometryRef.current = geometry;

        onChange({
          location: geometry,
          location_path_ids: pathIds,
        });

        setNetworkVisibility(currentMap, false);
        currentMap.getCanvas().style.cursor = "";
      };

      map.on("draw.create", syncFromDraw);
      map.on("draw.delete", syncFromDraw);

      map.on("draw.selectionchange", (e) => {
        if (e.features?.length) {
          drawRef.current.changeMode("simple_select", { featureIds: [] });
        }
      });
    });

    map.on("style.load", () => {
      ensureScenarioSourcesAndLayers(map);

      if (network) {
        addOrUpdateNetwork(map, network);
        setNetworkVisibility(map, false);
        attachHoverHandlers(map);
      }

      setStyleRevision((prev) => prev + 1);
    });

    return () => {
      const currentMap = mapRef.current;

      if (currentMap && networkHoverMoveHandlerRef.current) {
        currentMap.off("mousemove", NETWORK_LAYER_ID, networkHoverMoveHandlerRef.current);
        networkHoverMoveHandlerRef.current = null;
      }
      if (currentMap && networkHoverLeaveHandlerRef.current) {
        currentMap.off("mouseleave", NETWORK_LAYER_ID, networkHoverLeaveHandlerRef.current);
        networkHoverLeaveHandlerRef.current = null;
      }

      if (currentMap) {
        hoverPopupRef.current.remove();
        eventHoverPopupRef.current.remove();
        currentMap.remove();
      }

      mapRef.current = null;
      drawRef.current = null;
      setMapLoaded(false);
    };
  }, [onChange, network]);

  useEffect(() => {
    const map = mapRef.current;
    const draw = drawRef.current;

    if (!map || !mapLoaded || !draw || !isStyleReady(map)) return;

    geometryRef.current = value.location || null;
    draw.deleteAll();

    if (geometryRef.current) {
      draw.add({
        type: "Feature",
        properties: {},
        geometry: geometryRef.current,
      });
    } else {
      setNetworkVisibility(map, false);
    }
  }, [value.location, mapLoaded, styleRevision]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !network || !isStyleReady(map)) return;

    ensureScenarioSourcesAndLayers(map);
    addOrUpdateNetwork(map, network);
    setNetworkVisibility(map, false);
  }, [mapLoaded, network, styleRevision]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !isStyleReady(map)) return;

    ensureScenarioSourcesAndLayers(map);
    if (!map.getLayer(EVENTS_LAYER_ID)) return;

    if (!monitoringItems || monitoringItems.type !== "events") {
      addOrUpdateEvents(map, []);
      map.setLayoutProperty(EVENTS_LAYER_ID, "visibility", "none");
      eventHoverPopupRef.current.remove();
      return;
    }

    const allEvents = Array.isArray(monitoringItems.items) ? monitoringItems.items : [];

    if (!allEvents.length) {
      addOrUpdateEvents(map, []);
      map.setLayoutProperty(EVENTS_LAYER_ID, "visibility", "none");
      eventHoverPopupRef.current.remove();
      return;
    }

    const eventsWithSelected = allEvents.map((ev) => ({
      ...ev,
      selected: selectedIds.includes(ev.id),
    }));

    addOrUpdateEvents(map, eventsWithSelected);
    map.setLayoutProperty(EVENTS_LAYER_ID, "visibility", "visible");

    const handleMove = (e) => {
      if (!e.features?.length) return;
      const feature = e.features[0];
      const msg = feature.properties?.hoverMessage || feature.properties?.name || "Event";
      eventHoverPopupRef.current.setLngLat(e.lngLat).setHTML(msg).addTo(map);
    };

    const handleLeave = () => {
      eventHoverPopupRef.current.remove();
    };

    const handleClick = (e) => {
      const mode = activeModeRef.current;
      if (mode === "draw_line_string" || mode === "draw_point") return;
      if (!e.features?.length) return;

      const feature = e.features[0];
      const clickedId = feature.properties?.id;
      if (!clickedId) return;

      onChange({
        kpi_monitoring_ids: [clickedId],
      });
    };

    map.on("mousemove", EVENTS_LAYER_ID, handleMove);
    map.on("mouseleave", EVENTS_LAYER_ID, handleLeave);
    map.on("click", EVENTS_LAYER_ID, handleClick);

    return () => {
      if (!map.getLayer(EVENTS_LAYER_ID)) return;
      map.off("mousemove", EVENTS_LAYER_ID, handleMove);
      map.off("mouseleave", EVENTS_LAYER_ID, handleLeave);
      map.off("click", EVENTS_LAYER_ID, handleClick);
      eventHoverPopupRef.current.remove();
    };
  }, [mapLoaded, monitoringItems, selectedIds, onChange, styleRevision]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    const applyRoutesState = () => {
      if (!isStyleReady(map)) return;

      ensureScenarioSourcesAndLayers(map);
      if (!map.getSource(ROUTES_SOURCE_ID) || !map.getLayer(ROUTES_LAYER_ID)) return;

      if (!monitoringItems || monitoringItems.type !== "routes") {
        addOrUpdateRoutes(map, [], selectedIds);
        map.setLayoutProperty(ROUTES_LAYER_ID, "visibility", "none");
        hoverPopupRef.current.remove();
        return;
      }

      const allRoutes =
        Array.isArray(monitoringItems.items) && monitoringItems.items.length
          ? monitoringItems.items
          : routesData;

      addOrUpdateRoutes(map, allRoutes, selectedIds);
      map.setLayoutProperty(ROUTES_LAYER_ID, "visibility", "visible");

      const handleMove = (e) => {
        if (!e.features?.length) return;
        const feature = e.features[0];
        const msg =
          feature.properties?.hoverMessage || feature.properties?.route_name || "Route";
        hoverPopupRef.current.setLngLat(e.lngLat).setHTML(msg).addTo(map);
      };

      const handleLeave = () => {
        hoverPopupRef.current.remove();
      };

      const handleClick = (e) => {
        const mode = activeModeRef.current;
        if (mode === "draw_line_string" || mode === "draw_point") return;
        if (!e.features?.length) return;

        const feature = e.features[0];
        const clickedRouteId = feature.properties?.route_id;
        if (!clickedRouteId) return;

        const set = new Set(selectedIds || []);
        if (set.has(clickedRouteId)) set.delete(clickedRouteId);
        else set.add(clickedRouteId);

        onChange({
          kpi_monitoring_ids: Array.from(set),
        });
      };

      if (!map.getLayer(ROUTES_LAYER_ID)) return;
      map.off("mousemove", ROUTES_LAYER_ID, handleMove);
      map.off("mouseleave", ROUTES_LAYER_ID, handleLeave);
      map.off("click", ROUTES_LAYER_ID, handleClick);
      map.on("mousemove", ROUTES_LAYER_ID, handleMove);
      map.on("mouseleave", ROUTES_LAYER_ID, handleLeave);
      map.on("click", ROUTES_LAYER_ID, handleClick);
    };

    if (isStyleReady(map)) {
      applyRoutesState();
      return undefined;
    }

    map.once("idle", applyRoutesState);

    return () => {
      map.off("idle", applyRoutesState);
      hoverPopupRef.current.remove();
    };
  }, [mapLoaded, monitoringItems, selectedIds, onChange, styleRevision, routesData]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    const applyAreasState = () => {
      if (!isStyleReady(map)) return;

      ensureScenarioSourcesAndLayers(map);
      if (!map.getSource(AREAS_SOURCE_ID)) return;

      console.log("ScenarioMap areas effect", {
        monitoringType: monitoringItems?.type || null,
        monitoringItemsCount: Array.isArray(monitoringItems?.items)
          ? monitoringItems.items.length
          : null,
        areasDataCount: Array.isArray(areasData) ? areasData.length : null,
      });

      if (!monitoringItems || monitoringItems.type !== "areas") {
        addOrUpdateAreas(map, [], selectedIds);

        if (map.getLayer(AREAS_FILL_LAYER_ID)) {
          map.setLayoutProperty(AREAS_FILL_LAYER_ID, "visibility", "none");
        }
        if (map.getLayer(AREAS_OUTLINE_LAYER_ID)) {
          map.setLayoutProperty(AREAS_OUTLINE_LAYER_ID, "visibility", "none");
        }

        hoverPopupRef.current.remove();
        return;
      }

      const allAreas =
        Array.isArray(monitoringItems.items) && monitoringItems.items.length
          ? monitoringItems.items
          : areasData;

      addOrUpdateAreas(map, allAreas, selectedIds);

      if (map.getLayer(AREAS_FILL_LAYER_ID)) {
        map.setLayoutProperty(AREAS_FILL_LAYER_ID, "visibility", "visible");
      }
      if (map.getLayer(AREAS_OUTLINE_LAYER_ID)) {
        map.setLayoutProperty(AREAS_OUTLINE_LAYER_ID, "visibility", "visible");
      }

      const handleMove = (e) => {
        if (!e.features?.length) return;
        const feature = e.features[0];
        const msg = feature.properties?.hoverMessage || feature.properties?.name || "Area";
        hoverPopupRef.current.setLngLat(e.lngLat).setHTML(msg).addTo(map);
      };

      const handleLeave = () => {
        hoverPopupRef.current.remove();
      };

      const handleClick = (e) => {
        const mode = activeModeRef.current;
        if (mode === "draw_line_string" || mode === "draw_point") return;
        if (!e.features?.length) return;

        const feature = e.features[0];
        const clickedAreaId = feature.properties?.area_id;
        if (!clickedAreaId) return;

        const set = new Set(selectedIds || []);
        if (set.has(clickedAreaId)) set.delete(clickedAreaId);
        else set.add(clickedAreaId);

        onChange({
          kpi_monitoring_ids: Array.from(set),
        });
      };

      if (!map.getLayer(AREAS_FILL_LAYER_ID)) return;
      map.off("mousemove", AREAS_FILL_LAYER_ID, handleMove);
      map.off("mouseleave", AREAS_FILL_LAYER_ID, handleLeave);
      map.off("click", AREAS_FILL_LAYER_ID, handleClick);
      map.on("mousemove", AREAS_FILL_LAYER_ID, handleMove);
      map.on("mouseleave", AREAS_FILL_LAYER_ID, handleLeave);
      map.on("click", AREAS_FILL_LAYER_ID, handleClick);
    };

    if (isStyleReady(map)) {
      applyAreasState();
      return undefined;
    }

    map.once("idle", applyAreasState);

    return () => {
      map.off("idle", applyAreasState);
      hoverPopupRef.current.remove();
    };
  }, [mapLoaded, monitoringItems, selectedIds, onChange, styleRevision, areasData]);

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <div ref={mapContainerRef} style={scenarioMapConfig.container} />
    </Box>
  );
}

export default ScenarioMap;
